import ParlayCalculator from "@/components/ParlayCalculator";
import OddsConverter from "@/components/OddsConverter";
import HowItWorks from "@/components/HowItWorks";
import { useSEO } from "@/lib/useSEO";
import { Link } from "react-router-dom";
import { BookOpen, Repeat, ArrowRight } from "lucide-react";
import { EVENTS, track } from "@/lib/analytics";

const TITLE = "Free Parlay Calculator and Odds Converter | Parlay Assistant";
const DESCRIPTION =
  "Calculate parlay payouts, potential winnings, implied probability, and convert American, decimal, and fractional betting odds for free.";

// Popular Guides — five priority Playbook lessons deep-linked from the
// homepage with standard <a href> tags so they are crawlable regardless
// of client-side navigation. Slugs must match /app/frontend/src/data/playbook.js.
const POPULAR_GUIDES = [
  {
    slug: "parlay",
    title: "What Is a Parlay?",
    summary: "How multi-leg wagers work and why every leg usually has to win.",
  },
  {
    slug: "round-robin",
    title: "What Is a Round Robin Bet?",
    summary: "Turning several picks into smaller parlays — and what the total cost really is.",
  },
  {
    slug: "bankroll-management",
    title: "Bankroll Management, Simply",
    summary: "How to size wagers and protect the money set aside for betting.",
  },
  {
    slug: "straight-bet",
    title: "What Is a Straight Bet?",
    summary: "The one-selection wager every new bettor should understand first.",
  },
  {
    slug: "bonus-bets",
    title: "How Bonus Bets Actually Work",
    summary: "Why a $10 bonus bet is not always worth $10 in cash.",
  },
];

export const HomePage = () => {
  useSEO({ title: TITLE, description: DESCRIPTION, path: "/" });
  return (
    <>
      <main className="max-w-6xl mx-auto px-5 md:px-8 mt-10 md:mt-14">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
          <ParlayCalculator />
          <OddsConverter />
        </div>

        {/* Round Robin + Playbook discovery */}
        <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/round-robin-calculator"
            data-testid="home-round-robin-card"
            onClick={() =>
              track(EVENTS.PLAYBOOK_DISCOVERY_CARD_CLICKED, {
                card: "round-robin",
              })
            }
            className="pa-card p-6 md:p-7 group flex flex-col justify-between hover:-translate-y-0.5 transition-transform"
          >
            <div>
              <div
                className="inline-flex items-center justify-center w-11 h-11 rounded-xl"
                style={{
                  background: "linear-gradient(135deg,#065f46,#10b981)",
                  boxShadow: "0 8px 24px -8px rgba(16,185,129,0.6)",
                }}
              >
                <Repeat size={20} color="#ecfdf5" />
              </div>
              <p className="mt-4 font-display text-emerald-700 text-xs tracking-[0.2em] uppercase">
                New Tool
              </p>
              <h3 className="mt-1 font-display text-2xl md:text-3xl uppercase tracking-tight text-slate-900">
                Round Robin Calculator
              </h3>
              <p className="mt-2 text-sm md:text-base text-slate-600 max-w-md">
                Turn 3-12 selections into every smaller parlay combination. Split by amount per
                combo or total budget and see graded results including pushes and voids.
              </p>
            </div>
            <span className="mt-5 inline-flex items-center gap-2 font-display uppercase tracking-wider text-emerald-700 text-sm">
              Open Round Robin <ArrowRight size={16} />
            </span>
          </Link>

          <Link
            to="/playbook"
            data-testid="home-playbook-card"
            onClick={() =>
              track(EVENTS.PLAYBOOK_DISCOVERY_CARD_CLICKED, {
                card: "playbook",
              })
            }
            className="pa-card p-6 md:p-7 group flex flex-col justify-between hover:-translate-y-0.5 transition-transform"
          >
            <div>
              <div
                className="inline-flex items-center justify-center w-11 h-11 rounded-xl"
                style={{
                  background: "linear-gradient(135deg,#0f172a,#334155)",
                  boxShadow: "0 8px 24px -8px rgba(15,23,42,0.4)",
                }}
              >
                <BookOpen size={20} color="#f8fafc" />
              </div>
              <p className="mt-4 font-display text-slate-600 text-xs tracking-[0.2em] uppercase">
                Learn the Basics
              </p>
              <h3 className="mt-1 font-display text-2xl md:text-3xl uppercase tracking-tight text-slate-900">
                New to Sports Betting?
              </h3>
              <p className="mt-2 text-sm md:text-base text-slate-600 max-w-md">
                Fourteen short lessons cover straight bets, parlays, bankroll, promotions, and more
                — jump into whichever topic you need first.
              </p>
            </div>
            <span className="mt-5 inline-flex items-center gap-2 font-display uppercase tracking-wider text-slate-900 text-sm">
              Open the Playbook <ArrowRight size={16} />
            </span>
          </Link>
        </div>

        <HowItWorks />

        {/* Popular Guides — direct-linked with standard <a href> so
            crawlers can follow without executing SPA JS. */}
        <section
          aria-labelledby="popular-guides-heading"
          data-testid="home-popular-guides"
          className="mt-12 md:mt-16"
        >
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="font-display text-emerald-400 text-xs tracking-[0.25em] uppercase">
                Popular Guides
              </p>
              <h2
                id="popular-guides-heading"
                className="mt-1 font-display text-white text-2xl md:text-3xl uppercase tracking-tight"
              >
                Start with the Playbook
              </h2>
              <p className="mt-2 text-slate-300 text-sm md:text-base max-w-xl">
                Short, plain-language explainers on the wagers, bankroll rules,
                and sportsbook promotions bettors ask about most.
              </p>
            </div>
            <a
              href="/playbook"
              data-testid="home-popular-guides-index"
              className="inline-flex items-center gap-2 font-display uppercase tracking-wider text-emerald-400 hover:text-emerald-300 text-sm"
            >
              Browse all lessons
              <ArrowRight size={14} aria-hidden="true" />
            </a>
          </div>

          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {POPULAR_GUIDES.map((g) => (
              <li key={g.slug}>
                <a
                  href={`/playbook/${g.slug}`}
                  data-testid={`home-popular-guide-${g.slug}`}
                  className="pa-card p-5 h-full flex flex-col justify-between hover:-translate-y-0.5 transition-transform"
                >
                  <div>
                    <p className="font-display text-emerald-700 text-[10px] tracking-[0.25em] uppercase">
                      Playbook
                    </p>
                    <h3 className="mt-1 font-display text-lg md:text-xl uppercase tracking-tight text-slate-900 leading-tight">
                      {g.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">{g.summary}</p>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-2 font-display uppercase tracking-wider text-emerald-700 text-xs">
                    Read the guide <ArrowRight size={14} aria-hidden="true" />
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </>
  );
};

export default HomePage;
